// Extended delivery-queue filters (turbo_logistics.md §6.1).
import { SectionCard } from "@/components/common/SectionCard";
import { modeLabel, type SchedulingMode } from "@/lib/manufacturing/scheduledForPicker";
import { inputClass } from "./logisticsRoutesShared";
import { unitHandoffOptionLabel } from "@/lib/ops/handoffIdentity";
import {
  ORIGIN_STOP_KIND_FILTER_LABELS,
  ROUTE_STATE_LABELS,
} from "./logisticsRoutesShared";
import type { useLogisticsRoutesPanel } from "./useLogisticsRoutesPanel";

const WINDOW_FILTER_OPTIONS: SchedulingMode[] = [
  "morning",
  "afternoon",
  "specific_window",
  "specific_time",
  "date_only",
];

type Props = {
  m: ReturnType<typeof useLogisticsRoutesPanel>;
};

export function LogisticsRoutesQueueFilters({ m }: Props) {
  return (
    <SectionCard title="Queue filters">
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Unit
          <select
            className={`${inputClass} mt-1`}
            value={m.filter.unit_id}
            onChange={(e) => m.setFilter({ ...m.filter, unit_id: e.target.value })}
          >
            <option value="">All units</option>
            {m.units.map((u) => (
              <option key={u.id} value={u.id}>
                {unitHandoffOptionLabel(u)}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Carrier
          <select
            className={`${inputClass} mt-1`}
            value={m.filter.carrier_id}
            onChange={(e) => m.setFilter({ ...m.filter, carrier_id: e.target.value })}
          >
            <option value="">All carriers</option>
            {m.carriers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          State
          <select
            className={`${inputClass} mt-1`}
            value={m.filter.route_state}
            onChange={(e) => m.setFilter({ ...m.filter, route_state: e.target.value })}
          >
            <option value="">All states</option>
            {m.routeStateOptions.map((s) => (
              <option key={s} value={s}>
                {ROUTE_STATE_LABELS[s] ?? s}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Scheduled date
          <input
            type="date"
            className={`${inputClass} mt-1`}
            value={m.filter.scheduled_on}
            onChange={(e) => m.setFilter({ ...m.filter, scheduled_on: e.target.value })}
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Window
          <select
            className={`${inputClass} mt-1`}
            value={m.filter.scheduled_window_label}
            onChange={(e) =>
              m.setFilter({ ...m.filter, scheduled_window_label: e.target.value })
            }
          >
            <option value="">Any window</option>
            {WINDOW_FILTER_OPTIONS.map((mode) => (
              <option key={mode} value={modeLabel(mode)}>
                {modeLabel(mode)}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Origin
          <select
            className={`${inputClass} mt-1`}
            value={m.filter.origin_stop_kind}
            onChange={(e) => m.setFilter({ ...m.filter, origin_stop_kind: e.target.value })}
          >
            <option value="">Any origin</option>
            {Object.entries(ORIGIN_STOP_KIND_FILTER_LABELS).map(([kind, label]) => (
              <option key={kind} value={kind}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>
    </SectionCard>
  );
}
